import { Children, type ComponentProps, isValidElement, type ReactNode, useMemo } from 'react';

import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { type MasonryGridLayoutProps, MeasuredMasonryGrid } from './measured-masonry-grid';

type MasonryColumnCount = 1 | 2 | 3;

interface MasonryGridProps extends ComponentProps<'div'> {
  children: ReactNode;
  columns?: MasonryColumnCount;
  gap?: 'sm' | 'md' | 'lg';
}

const gapValueMap = {
  sm: 8,
  md: 16,
  lg: 24,
} as const;

const fallbackColumnClass: Record<MasonryColumnCount, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
};

function getItemsLayoutKey(items: ReactNode[]) {
  return items
    .map((child, index) => {
      if (isValidElement(child) && child.key != null) {
        return String(child.key);
      }

      return String(index);
    })
    .join('\0');
}

function SingleColumnMasonryGrid({
  items,
  itemsLayoutKey: _itemsLayoutKey,
  className,
  ...props
}: MasonryGridLayoutProps) {
  return (
    <div className={cn('flex w-full flex-col', className)} {...props}>
      {items}
    </div>
  );
}

function ResponsiveMasonryFallback({
  items,
  itemsLayoutKey: _itemsLayoutKey,
  columns,
  className,
  ...props
}: MasonryGridLayoutProps & { columns: MasonryColumnCount }) {
  return (
    <div
      className={cn('grid w-full items-start', fallbackColumnClass[columns], className)}
      {...props}
    >
      {items}
    </div>
  );
}

export function MasonryGrid({
  children,
  columns = 2,
  gap = 'md',
  className,
  style,
  ...props
}: MasonryGridProps) {
  const gapValue = gapValueMap[gap];
  const isMobile = useIsMobile();

  const items = useMemo(() => Children.toArray(children), [children]);
  const itemsLayoutKey = getItemsLayoutKey(items);

  const layoutProps = {
    items,
    itemsLayoutKey,
    className,
    ...props,
    style: { ...style, gap: gapValue },
  };

  if (columns === 1 || isMobile === true) {
    return <SingleColumnMasonryGrid {...layoutProps} />;
  }

  if (isMobile === undefined) {
    return <ResponsiveMasonryFallback {...layoutProps} columns={columns} />;
  }

  return <MeasuredMasonryGrid {...layoutProps} gapValue={gapValue} columns={columns} />;
}

interface MasonryGridItemProps extends ComponentProps<'div'> {
  children: ReactNode;
}

export function MasonryGridItem({ children, className, ...props }: MasonryGridItemProps) {
  return (
    <div className={cn('w-full', className)} {...props}>
      {children}
    </div>
  );
}
